import React, { useState, useEffect } from "react";
import "./AdminDashboard.css";
import { db, auth } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  where,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { getIdToken } from "firebase/auth";

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState("updates");
  const [updates, setUpdates] = useState([]);
  const [parents, setParents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUpdate, setCurrentUpdate] = useState({
    id: "",
    title: "",
    content: "",
    date: "",
    author: user?.name || "Admin",
    imageUrl: "",
    sourceLink: "",
  });
  const [loading, setLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    id: null,
    collection: null,
  });

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Load data from Firebase
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch updates
        const updatesQuery = query(
          collection(db, "updates"),
          orderBy("date", "desc"),
        );
        const updatesSnapshot = await getDocs(updatesQuery);
        const updatesData = updatesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUpdates(updatesData);

        // Fetch parents
        const parentsQuery = query(
          collection(db, "parents"),
          orderBy("createdAt", "desc"),
        );
        const parentsSnapshot = await getDocs(parentsQuery);
        const parentsData = parentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setParents(parentsData);

        // Fetch tutors
        const tutorsQuery = query(
          collection(db, "tutors"),
          orderBy("createdAt", "desc"),
        );
        const tutorsSnapshot = await getDocs(tutorsQuery);
        const tutorsData = tutorsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTutors(tutorsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Clear selected items when switching tabs
  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
    setSearchTerm("");
    setSortConfig({ key: null, direction: "asc" });
  }, [activeTab]);

  // Apply date filter for updates
  const filteredUpdates = updates.filter((update) => {
    if (!dateFilter.startDate && !dateFilter.endDate) return true;

    let updateDate;
    try {
      if (update.date && typeof update.date.toDate === "function") {
        updateDate = update.date.toDate();
      } else if (update.date) {
        updateDate = new Date(update.date);
      } else {
        return true;
      }

      const startDate = dateFilter.startDate
        ? new Date(dateFilter.startDate)
        : null;
      const endDate = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

      if (startDate && endDate) {
        return updateDate >= startDate && updateDate <= endDate;
      } else if (startDate) {
        return updateDate >= startDate;
      } else if (endDate) {
        return updateDate <= endDate;
      }
    } catch (error) {
      console.error("Error filtering date:", error);
      return true;
    }

    return true;
  });

  // Format date for input field
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";

    try {
      let date;
      if (typeof dateValue.toDate === "function") {
        date = dateValue.toDate();
      } else {
        date = new Date(dateValue);
      }

      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateValue);
        return "";
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date for input:", error);
      return "";
    }
  };

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return "Date not available";

    try {
      let date;
      if (typeof dateValue.toDate === "function") {
        date = dateValue.toDate();
      } else {
        date = new Date(dateValue);
      }

      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateValue);
        return "Invalid date";
      }

      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";

      hours = hours % 12;
      hours = hours ? hours : 12;

      return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error("Error formatting date for display:", error);
      return "Date error";
    }
  };

  // Search functionality
  const getFilteredData = () => {
    let data = [];
    if (activeTab === "parents") {
      data = parents;
    } else if (activeTab === "tutors") {
      data = tutors;
    }

    if (!searchTerm) return data;

    return data.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      return Object.values(item).some(
        (value) =>
          value && value.toString().toLowerCase().includes(searchLower),
      );
    });
  };

  // Sort functionality
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle dates
      if (sortConfig.key === "createdAt") {
        aValue =
          aValue && typeof aValue.toDate === "function"
            ? aValue.toDate()
            : new Date(aValue || 0);
        bValue =
          bValue && typeof bValue.toDate === "function"
            ? bValue.toDate()
            : new Date(bValue || 0);
      }

      // Handle strings
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue ? bValue.toLowerCase() : "";
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Handle select all
  const handleSelectAll = () => {
    const data = getSortedData(getFilteredData());
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(data.map((item) => item.id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual item selection
  const handleItemSelect = (id) => {
    setSelectedItems((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // CSV Export functionality
  const exportToCSV = () => {
    const data = getSortedData(getFilteredData());
    if (data.length === 0) {
      alert("No data to export");
      return;
    }

    let csvContent = "";
    let headers = [];
    let rows = [];

    if (activeTab === "parents") {
      headers = [
        "Name",
        "Mobile",
        "Location",
        "Child Details",
        "Preferred Timing",
        "Fee",
        "Mode",
        "Registration Date",
      ];
      rows = data.map((parent) => [
        parent.name || "",
        parent.mobile || "",
        parent.location || "",
        parent.childDetails || "",
        parent.timing || "",
        parent.fee || "",
        parent.mode || "",
        parent.createdAt ? formatDate(parent.createdAt) : "",
      ]);
    } else if (activeTab === "tutors") {
      headers = [
        "Name",
        "Mobile",
        "Secondary Contact",
        "Location",
        "Qualifications",
        "Experience",
        "Subjects",
        "Registration Date",
      ];
      rows = data.map((tutor) => [
        tutor.name || "",
        tutor.mobile || "",
        tutor.secondaryContact || "",
        tutor.location || "",
        tutor.qualifications || "",
        tutor.experience || "",
        tutor.subjects || "",
        tutor.createdAt ? formatDate(tutor.createdAt) : "",
      ]);
    }

    // Escape CSV values and wrap in quotes if they contain commas, quotes, or newlines
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return "";
      const stringValue = String(value);
      if (
        stringValue.includes(",") ||
        stringValue.includes('"') ||
        stringValue.includes("\n")
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Create CSV content
    csvContent = headers.map(escapeCSV).join(",") + "\n";
    csvContent += rows.map((row) => row.map(escapeCSV).join(",")).join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${activeTab}_data_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Bulk delete functionality
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert("Please select items to delete");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedItems.length} selected items? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      // Delete selected items from Firebase
      const deletePromises = selectedItems.map((id) =>
        deleteDoc(doc(db, activeTab, id)),
      );
      await Promise.all(deletePromises);

      // Update local state
      if (activeTab === "parents") {
        setParents(
          parents.filter((parent) => !selectedItems.includes(parent.id)),
        );
      } else if (activeTab === "tutors") {
        setTutors(tutors.filter((tutor) => !selectedItems.includes(tutor.id)));
      }

      setSelectedItems([]);
      setSelectAll(false);
      alert(`Successfully deleted ${selectedItems.length} items`);
    } catch (error) {
      console.error("Error deleting items:", error);
      alert("Error deleting items: " + error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentUpdate({ ...currentUpdate, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentUpdate({ ...currentUpdate, imageUrl: reader.result });
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilter({ ...dateFilter, [name]: value });
  };

  const clearDateFilter = () => {
    setDateFilter({ startDate: "", endDate: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!auth.currentUser) {
        throw new Error("You are not authenticated. Please log in again.");
      }

      try {
        await getIdToken(auth.currentUser, true);
      } catch (tokenError) {
        console.error("Error refreshing auth token:", tokenError);
        throw new Error("Authentication error. Please log in again.");
      }

      let dateValue;
      if (currentUpdate.date) {
        try {
          dateValue = new Date(currentUpdate.date);
          if (isNaN(dateValue.getTime())) {
            throw new Error("Invalid date format");
          }
        } catch (dateError) {
          console.error("Date parsing error:", dateError);
          dateValue = new Date();
        }
      } else {
        dateValue = new Date();
      }

      const updatedPost = {
        title: currentUpdate.title,
        content: currentUpdate.content,
        author: user?.name || "Admin",
        imageUrl: currentUpdate.imageUrl || "",
        sourceLink: currentUpdate.sourceLink || "",
        date: dateValue,
        serverTime: serverTimestamp(),
        updatedBy: auth.currentUser.uid,
        updatedAt: serverTimestamp(),
      };

      if (isEditing) {
        await updateDoc(doc(db, "updates", currentUpdate.id), updatedPost);
        const updatedPostWithId = { ...updatedPost, id: currentUpdate.id };
        setUpdates(
          updates.map((update) =>
            update.id === currentUpdate.id ? updatedPostWithId : update,
          ),
        );
      } else {
        const docRef = await addDoc(collection(db, "updates"), updatedPost);
        const newPostWithId = { ...updatedPost, id: docRef.id };
        setUpdates([newPostWithId, ...updates]);
      }

      setCurrentUpdate({
        id: "",
        title: "",
        content: "",
        date: "",
        author: user?.name || "Admin",
        imageUrl: "",
        sourceLink: "",
      });
      setIsEditing(false);
      setImagePreview(null);
    } catch (error) {
      console.error("Error saving update:", error);

      if (error.code === "permission-denied") {
        alert(
          "Error: You don't have permission to perform this action. Please verify you are logged in with the correct admin account.",
        );
      } else if (error.message && error.message.includes("permission")) {
        alert(
          "Error: Missing or insufficient permissions. Please log out and log back in, or contact your administrator.",
        );
      } else {
        alert("Error saving update: " + error.message);
      }
    }
  };

  const handleEdit = (update) => {
    setCurrentUpdate(update);
    setIsEditing(true);
    setImagePreview(update.imageUrl);
    setActiveTab("updates");

    document
      .querySelector(".admin-form-container")
      .scrollIntoView({ behavior: "smooth" });
  };

  const showDeleteConfirm = (id, collection) => {
    setDeleteConfirm({ show: true, id, collection });
  };

  const hideDeleteConfirm = () => {
    setDeleteConfirm({ show: false, id: null, collection: null });
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, deleteConfirm.collection, deleteConfirm.id));

      if (deleteConfirm.collection === "updates") {
        setUpdates(updates.filter((update) => update.id !== deleteConfirm.id));
      } else if (deleteConfirm.collection === "parents") {
        setParents(parents.filter((parent) => parent.id !== deleteConfirm.id));
      } else if (deleteConfirm.collection === "tutors") {
        setTutors(tutors.filter((tutor) => tutor.id !== deleteConfirm.id));
      }

      hideDeleteConfirm();
    } catch (error) {
      console.error(
        `Error deleting ${deleteConfirm.collection} record:`,
        error,
      );
      alert(`Error deleting record: ${error.message}`);
      hideDeleteConfirm();
    }
  };

  const handleCancel = () => {
    setCurrentUpdate({
      id: "",
      title: "",
      content: "",
      date: "",
      author: user?.name || "Admin",
      imageUrl: "",
      sourceLink: "",
    });
    setIsEditing(false);
    setImagePreview(null);
  };

  const handleRemoveImage = () => {
    setCurrentUpdate({ ...currentUpdate, imageUrl: "" });
    setImagePreview(null);
  };

  // Get current data for table display
  const getCurrentData = () => {
    return getSortedData(getFilteredData());
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort sort-icon"></i>;
    }
    return sortConfig.direction === "asc" ? (
      <i className="fas fa-sort-up sort-icon active"></i>
    ) : (
      <i className="fas fa-sort-down sort-icon active"></i>
    );
  };

  return (
    <div className="admin-dashboard">
      {/* Delete Confirmation Popup */}
      {deleteConfirm.show && (
        <div className="delete-popup-overlay">
          <div className="delete-popup">
            <div className="delete-popup-header">
              <h3>Confirm Deletion</h3>
            </div>
            <div className="delete-popup-content">
              <p>
                Are you sure you want to delete this{" "}
                {deleteConfirm.collection === "updates"
                  ? "update"
                  : deleteConfirm.collection === "parents"
                    ? "parent record"
                    : "tutor record"}
                ?
              </p>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>
            <div className="delete-popup-actions">
              <button className="cancel-action" onClick={hideDeleteConfirm}>
                Cancel
              </button>
              <button className="confirm-action" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-header">
        <div className="admin-title">
          <h1>Admin Dashboard</h1>
          <p>Welcome, {user?.name || "Admin"}</p>
        </div>
        <button className="logout-btn" onClick={onLogout}>
          Logout
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === "updates" ? "active" : ""}`}
          onClick={() => setActiveTab("updates")}
        >
          Updates
        </button>
        <button
          className={`tab-btn ${activeTab === "parents" ? "active" : ""}`}
          onClick={() => setActiveTab("parents")}
        >
          Parents ({parents.length})
        </button>
        <button
          className={`tab-btn ${activeTab === "tutors" ? "active" : ""}`}
          onClick={() => setActiveTab("tutors")}
        >
          Tutors ({tutors.length})
        </button>
      </div>

      {activeTab === "updates" && (
        <div className="admin-container">
          <div className="admin-form-container">
            <h2>{isEditing ? "Edit Update" : "Add New Update"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={currentUpdate.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter update title"
                />
              </div>

              <div className="form-group">
                <label htmlFor="content">Content</label>
                <textarea
                  id="content"
                  name="content"
                  value={currentUpdate.content}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter update content"
                  rows="8"
                ></textarea>
                <small>Use new lines to separate paragraphs.</small>
              </div>

              <div className="form-group">
                <label htmlFor="image">Image (Optional)</label>
                <div className="image-upload-container">
                  <input
                    type="file"
                    id="image"
                    name="image"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="image-input"
                  />
                  <label htmlFor="image" className="image-upload-label">
                    <i className="fas fa-cloud-upload-alt"></i> Choose Image
                  </label>
                </div>

                {imagePreview && (
                  <div className="image-preview-container">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="image-preview"
                    />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={handleRemoveImage}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="sourceLink">Source Link (Optional)</label>
                <input
                  type="url"
                  id="sourceLink"
                  name="sourceLink"
                  value={currentUpdate.sourceLink || ""}
                  onChange={handleInputChange}
                  placeholder="Enter source URL (e.g., https://example.com)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="date">Date and Time</label>
                <input
                  type="datetime-local"
                  id="date"
                  name="date"
                  value={formatDateForInput(currentUpdate.date)}
                  onChange={handleInputChange}
                />
                <small>Leave blank to use current date and time</small>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  {isEditing ? "Update" : "Add"} Post
                </button>
                {isEditing && (
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="admin-list">
            <div className="list-header">
              <h2>Updates List</h2>
              <div className="date-filter">
                <div className="filter-group">
                  <label htmlFor="startDate">From:</label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={dateFilter.startDate}
                    onChange={handleDateFilterChange}
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="endDate">To:</label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={dateFilter.endDate}
                    onChange={handleDateFilterChange}
                  />
                </div>
                <button
                  type="button"
                  className="clear-filter-btn"
                  onClick={clearDateFilter}
                >
                  Clear
                </button>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading updates...</div>
            ) : filteredUpdates.length === 0 ? (
              <div className="no-data">No updates found.</div>
            ) : (
              <div className="updates-list">
                {filteredUpdates.map((update) => (
                  <div key={update.id} className="update-item">
                    <div className="update-content">
                      <h3>{update.title}</h3>
                      <p className="update-meta">
                        <span className="update-author">
                          By {update.author}
                        </span>
                        <span className="update-date">
                          {formatDate(update.date)}
                        </span>
                      </p>
                      <div className="update-text">
                        {update.content.split("\n").map((paragraph, i) => (
                          <p key={i}>{paragraph}</p>
                        ))}
                      </div>
                      {update.imageUrl && (
                        <div className="update-image">
                          <img src={update.imageUrl} alt={update.title} />
                        </div>
                      )}
                      {update.sourceLink && (
                        <a
                          href={update.sourceLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="source-link"
                        >
                          Source
                        </a>
                      )}
                    </div>
                    <div className="update-actions">
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(update)}
                      >
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => showDeleteConfirm(update.id, "updates")}
                      >
                        <i className="fas fa-trash-alt"></i> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "parents" && (
        <div className="admin-full-width-container">
          <div className="admin-list">
            <div className="table-header">
              <h2>Parents Registrations</h2>
              <div className="table-controls">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search parents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <i className="fas fa-search search-icon"></i>
                </div>
                <div className="action-buttons">
                  {selectedItems.length > 0 && (
                    <button
                      className="bulk-delete-btn"
                      onClick={handleBulkDelete}
                    >
                      <i className="fas fa-trash-alt"></i> Delete Selected (
                      {selectedItems.length})
                    </button>
                  )}
                  <button className="export-btn" onClick={exportToCSV}>
                    <i className="fas fa-download"></i> Export CSV
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading parents data...</div>
            ) : getCurrentData().length === 0 ? (
              <div className="no-data">
                {searchTerm
                  ? `No parents found matching "${searchTerm}"`
                  : "No parent registrations found."}
              </div>
            ) : (
              <div className="enhanced-table-container">
                <table className="enhanced-table">
                  <thead>
                    <tr>
                      <th className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("name")}
                      >
                        Name {renderSortIcon("name")}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("mobile")}
                      >
                        Mobile {renderSortIcon("mobile")}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("location")}
                      >
                        Location {renderSortIcon("location")}
                      </th>
                      <th>Child Details</th>
                      <th>Preferred Timing</th>
                      <th>Fee</th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("mode")}
                      >
                        Mode {renderSortIcon("mode")}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("createdAt")}
                      >
                        Date {renderSortIcon("createdAt")}
                      </th>
                      <th className="actions-column">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getCurrentData().map((parent) => (
                      <tr
                        key={parent.id}
                        className={
                          selectedItems.includes(parent.id) ? "selected" : ""
                        }
                      >
                        <td className="checkbox-column">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(parent.id)}
                            onChange={() => handleItemSelect(parent.id)}
                          />
                        </td>
                        <td className="name-cell">{parent.name}</td>
                        <td className="mobile-cell">{parent.mobile}</td>
                        <td className="location-cell">{parent.location}</td>
                        <td className="details-cell">{parent.childDetails}</td>
                        <td className="timing-cell">{parent.timing}</td>
                        <td className="fee-cell">{parent.fee}</td>
                        <td className="mode-cell">
                          <span
                            className={`mode-badge ${parent.mode?.toLowerCase()}`}
                          >
                            {parent.mode}
                          </span>
                        </td>
                        <td className="date-cell">
                          {parent.createdAt
                            ? formatDate(parent.createdAt)
                            : "Date not available"}
                        </td>
                        <td className="actions-column">
                          <button
                            className="delete-btn small"
                            onClick={() =>
                              showDeleteConfirm(parent.id, "parents")
                            }
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "tutors" && (
        <div className="admin-full-width-container">
          <div className="admin-list">
            <div className="table-header">
              <h2>Tutors Registrations</h2>
              <div className="table-controls">
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search tutors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <i className="fas fa-search search-icon"></i>
                </div>
                <div className="action-buttons">
                  {selectedItems.length > 0 && (
                    <button
                      className="bulk-delete-btn"
                      onClick={handleBulkDelete}
                    >
                      <i className="fas fa-trash-alt"></i> Delete Selected (
                      {selectedItems.length})
                    </button>
                  )}
                  <button className="export-btn" onClick={exportToCSV}>
                    <i className="fas fa-download"></i> Export CSV
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading tutors data...</div>
            ) : getCurrentData().length === 0 ? (
              <div className="no-data">
                {searchTerm
                  ? `No tutors found matching "${searchTerm}"`
                  : "No tutor registrations found."}
              </div>
            ) : (
              <div className="enhanced-table-container">
                <table className="enhanced-table">
                  <thead>
                    <tr>
                      <th className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("name")}
                      >
                        Name {renderSortIcon("name")}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("mobile")}
                      >
                        Mobile {renderSortIcon("mobile")}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("secondaryContact")}
                      >
                        Secondary Contact {renderSortIcon("secondaryContact")}
                      </th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("location")}
                      >
                        Location {renderSortIcon("location")}
                      </th>
                      <th>Qualifications</th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("experience")}
                      >
                        Experience {renderSortIcon("experience")}
                      </th>
                      <th>Subjects</th>
                      <th
                        className="sortable"
                        onClick={() => handleSort("createdAt")}
                      >
                        Date {renderSortIcon("createdAt")}
                      </th>
                      <th className="actions-column">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getCurrentData().map((tutor) => (
                      <tr
                        key={tutor.id}
                        className={
                          selectedItems.includes(tutor.id) ? "selected" : ""
                        }
                      >
                        <td className="checkbox-column">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(tutor.id)}
                            onChange={() => handleItemSelect(tutor.id)}
                          />
                        </td>
                        <td className="name-cell">{tutor.name}</td>
                        <td className="mobile-cell">{tutor.mobile}</td>
                        <td className="contact-cell">
                          {tutor.secondaryContact || "N/A"}
                        </td>
                        <td className="location-cell">{tutor.location}</td>
                        <td className="qualifications-cell">
                          {tutor.qualifications}
                        </td>
                        <td className="experience-cell">{tutor.experience}</td>
                        <td className="subjects-cell">{tutor.subjects}</td>
                        <td className="date-cell">
                          {tutor.createdAt
                            ? formatDate(tutor.createdAt)
                            : "Date not available"}
                        </td>
                        <td className="actions-column">
                          <button
                            className="delete-btn small"
                            onClick={() =>
                              showDeleteConfirm(tutor.id, "tutors")
                            }
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
